import { css } from '@emotion/react';
import {
  BalanceType,
  GetPatientsBalanceResponse_PatientBalance,
} from '@weave/schema-gen-ts/dist/schemas/payments/messages/commands/commands.pb';
import { FeatureFlagQueries } from '@frontend/api-feature-flags';
import { Chips } from '@frontend/chips';
import { i18next } from '@frontend/i18n';
import { CollectPaymentMultiModalInstance } from '@frontend/payments-collection-flow';
import { PaymentsFeatureFlags } from '@frontend/payments-hooks';
import { PaymentModal } from '@frontend/person-invoice-payment';
import { useAppScopeStore } from '@frontend/scope';
import { Button, Chip, ChipProps, TableColumnConfig, Text, useModalControl } from '@frontend/design-system';
import { formatCentsToCurrency, GetLocationName } from '../../../utils';

export type CollectionColumn = GetPatientsBalanceResponse_PatientBalance;

export const generateColumns = (getLocationName: GetLocationName): TableColumnConfig<CollectionColumn>[] => {
  return [
    {
      id: 'name',
      Header: 'Patient Name',
      accessor: (collection) => `${collection?.firstName}#${collection?.lastName}`,
      cellRenderer: (fullName) => {
        const [FirstName, LastName] = fullName.split('#');
        return (
          <>
            <Text weight='bold'>{FirstName}</Text>&nbsp;
            <Text>{LastName}</Text>
          </>
        );
      },
    },
    {
      id: 'balance',
      Header: 'Balance',
      accessor: (collection) => collection?.balance,
      cellRenderer: (Balance) => formatCentsToCurrency(Balance),
    },
    {
      id: 'location',
      Header: 'Location',
      accessor: (collection) => collection?.locationIds,
      cellRenderer: (locationIds) => (
        <div>
          {locationIds.map((locationId: string) => (
            <Chips.LocationChip
              key={locationId}
              css={css`
                max-width: none;
              `}
            >
              {getLocationName(locationId ?? '')}
            </Chips.LocationChip>
          ))}
        </div>
      ),
      disableSortBy: true,
    },
    {
      id: 'balanceType',
      Header: 'Balance Type',
      accessor: (collection) => collection.balanceType,
      cellRenderer: (balanceType) => <BalanceTag balanceType={balanceType} />,
      disableSortBy: true,
    },
    {
      id: 'actions',
      Header: '',
      accessor: (collection) => collection.patientId,
      disableSortBy: true,
      cellRenderer: (id) => <Actions personId={id} />,
      width: 50,
    },
  ];
};

const BalanceTag = ({ balanceType }: { balanceType: BalanceType }) => {
  let color: ChipProps['variant'] = 'default',
    label = i18next.t('Unknown', { ns: 'payments' });
  switch (balanceType) {
    case BalanceType.BALANCE_TYPE_NEW: {
      color = 'seaweed';
      label = i18next.t('New Balance', { ns: 'payments' });
      break;
    }
    case BalanceType.BALANCE_TYPE_UPDATE: {
      color = 'eggplant';
      label = i18next.t('Changed Balance', { ns: 'payments' });
      break;
    }
    case BalanceType.BALANCE_TYPE_EXISTS: {
      color = 'warn';
      label = i18next.t('Existing Balance', { ns: 'payments' });
      break;
    }
  }
  if (!balanceType) return null;
  return <Chip variant={color}>{label}</Chip>;
};

const Actions = ({ personId }: { personId: string }) => {
  const { selectedLocationIds } = useAppScopeStore();
  const { modalProps: createModalProps, triggerProps: createModalTriggerProps } = useModalControl();

  const { aggregateValue: showMultiProcessorCollect } = FeatureFlagQueries.useAggregateFeatureFlagQuery({
    flagName: PaymentsFeatureFlags.UseMultiProcessorCollect,
    locationIds: selectedLocationIds,
  });

  return (
    <>
      <div>
        <Button
          {...createModalTriggerProps}
          iconName='dollar-sign'
          variant='secondary'
          size='large'
          hoverLabel='Collect Payment'
        />
      </div>
      {showMultiProcessorCollect ? (
        <CollectPaymentMultiModalInstance
          modalProps={createModalProps}
          collectProps={{
            personId,
            locationId: selectedLocationIds[0],
            flow: 'select',
          }}
        />
      ) : (
        <PaymentModal {...createModalProps} personId={personId} />
      )}
    </>
  );
};
